import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/reducers';
import { Router } from '@angular/router';
import * as AutomationActions from './actions';
import { catchError, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { AutomationFlowConfig } from '../../interfaces/automation-flow-config.interface';
import { Noop } from '../../state/actions';
import { AutomationService } from '../automation.service';
import { getCurrentPractice } from '../../practices/state/selectors';
import { AutomationRule } from '../../interfaces/automation-rule.interface';
import { MessageService } from 'primeng/api';
import { HttpResponse } from '@angular/common/http';
import { AutomationRulesResult } from 'src/app/interfaces/automation-rule-result.interface';

@Injectable()
export class AutomationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    public messageService: MessageService,
    private automationService: AutomationService
  ) {}

  getFlowConfigurations$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.GetFlowConfigurations),
    mergeMap((action) => {
      return this.automationService.getFlowConfigurations()
        .pipe(
          map((result: AutomationFlowConfig[]) => {
            return AutomationActions.GetFlowConfigurationsSuccess({configs: result});
          }),
          catchError(() => {
            // this.messageService.add({
            //   severity: 'error',
            //   summary: 'Error',
            //   detail: 'An error occurred when getting templates',
            //   life: 5000
            // });
            return of(Noop());
          })
        );
    })
  ));

  getFlowRules$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.GetFlowRules),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.getRules(action.flow, practice!.id, action.page, action.perPage, action?.status)
      .pipe(
        map((result: AutomationRulesResult) => {
          return AutomationActions.GetFlowRulesSuccess({rules: result.rules, 
            total: result.total, 
            page: result.page, 
            perPage: result.perPage, 
            totalPages: result.totalPages, 
            currentPage: result.currentPage, 
            hasMore: result.hasMore 
          });
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to fetch the flows for this clinic",
            life: 10000,
          });
          return of(AutomationActions.GetFlowRulesFail());
        })
      ))
    )
  );

  deleteRule$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.DeleteRule),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.deleteRule(action.id, practice!.id)
      .pipe(
        map((result: boolean) => {
          return AutomationActions.DeleteRuleSuccess({id: action.id});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to delete this rule",
            life: 10000,
          });
          return of(AutomationActions.DeleteRuleFail());
        })
      ))
    )
  );

  setDryRunStatus$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.SetDryRunStatus),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.setDryRunStatus(action.id, action.dryRun, practice!.id)
      .pipe(
        map((result: boolean) => {
          return AutomationActions.SetDryRunStatusSuccess({id: action.id, dryRun: action.dryRun});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to update this rule",
            life: 10000,
          });
          return of(AutomationActions.SetDryRunStatusFail());
        })
      ))
    )
  );

  saveRule$ = createEffect(() => this.actions$.pipe(
    ofType(AutomationActions.SaveRule),
    withLatestFrom(this.store.select(getCurrentPractice)),
    filter(([action, practice]) => practice !== null),
    mergeMap(([action, practice]) => this.automationService.saveRule(action.body, practice!.id)
      .pipe(
        map((result: AutomationRule) => {
          return AutomationActions.SaveRuleSuccess({rule: result});
        }),
        catchError((error) => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              "An error occured when trying to save this rule",
            life: 10000,
          });
          return of(AutomationActions.SaveRuleFail());
        })
      ))
    )
  );

  SaveRuleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.SaveRuleSuccess),
      tap((action) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Automation Rule Saved',
          detail: 'The automation rule has been saved successfully.',
          life: 5000,
        });
      }),
      map((action) => {
        return Noop();
      })
    )
  );

  DeleteRuleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.DeleteRuleSuccess),
      tap((action) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Automation Rule Deleted',
          detail: 'The automation rule has been deleted successfully.',
          life: 5000,
        });
      }),
      map((action) => {
        return Noop();
      })
    )
  );

  exportDryrunData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutomationActions.ExportDryrunData),
      switchMap(action =>
        this.automationService.getDryRunExport(
          action.flowConfig,
          action.ruleIds,
          action.sendAtFrom,
          action.sendAtTo,
          action.practiceId
        ).pipe(
          map((response: HttpResponse<Blob>) => {
            // Handle the file download here
            this.handleFileDownload(response);
            return AutomationActions.ExportDryrunDataSuccess();
          }),
          catchError(error => {
            this.messageService.add({
              severity: 'error',
              summary: 'Export Failed',
              detail: 'Failed to export dry run data. Please try again.',
              life: 5000
            });
            return of(AutomationActions.ExportDryrunDataFailure({ error }));
          })
        )
      )
    )
  );

  private handleFileDownload(response: HttpResponse<Blob>): void {
    if (response.body) {
      // Extract filename from Content-Disposition header or create default
      let filename = `dry-run-export-${new Date().toISOString().split('T')[0]}.csv`;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create and trigger download
      const url = window.URL.createObjectURL(response.body);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(url);

      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'Export Successful',
        detail: 'Dry run data has been exported successfully',
        life: 3000
      });
    }
  }
}
